/**
 * Every cache key in one place, with no framework in it (Phase 1I).
 *
 * It lived in `useQueries.js`, which imports Vue — so a React hook could not
 * reach it without dragging Vue in, and the alternative, retyping the array, is
 * how two caches for one thing come about. `useQueries.js` re-exports this, so
 * its ~30 existing importers are untouched.
 *
 * The shapes are load-bearing: `useCacheInvalidation.js` matches on prefixes
 * (`['scenarioCosts', id]`), so the order of the parts decides what an
 * invalidation reaches.
 */
export const queryKeys = {
  // Scenario costs: ['scenarioCosts', scenarioId, resultType, workspaceId]
  scenarioCosts: (scenarioId, resultType = 'optimization', workspaceId = 0) => 
    ['scenarioCosts', scenarioId, resultType, workspaceId],
  
  // All scenario costs for a workspace
  allScenarioCosts: (workspaceId = 0) => ['scenarioCosts', { workspaceId }],
  
  // Network data: ['networkData', scenarioId, mode, workspaceId]
  networkData: (scenarioId, mode = 'baseline', workspaceId = 0) => 
    ['networkData', scenarioId, mode, workspaceId],
  
  // All network data for a workspace
  allNetworkData: (workspaceId = 0) => ['networkData', { workspaceId }],
  
  // Scenarios list: ['scenarios', workspaceId]
  scenarios: (workspaceId = 0) => ['scenarios', workspaceId],
  
  // Facilities breakdown: ['facilitiesBreakdown', scenarioId, resultType, workspaceId]
  facilitiesBreakdown: (scenarioId, resultType = 'optimization', workspaceId = 0) => 
    ['facilitiesBreakdown', scenarioId, resultType, workspaceId],
  
  // Facility flows: ['facilityFlows', facilityName, scenarioId, workspaceId]
  facilityFlows: (facilityName, scenarioId, workspaceId = 0) => 
    ['facilityFlows', facilityName, scenarioId, workspaceId],
  
  // Purchase method stats: ['purchaseMethodStats', scenarioId, workspaceId]
  purchaseMethodStats: (scenarioId, workspaceId = 0) => 
    ['purchaseMethodStats', scenarioId, workspaceId],
  
  // Optimization config: ['optimizationConfig', scenarioId, workspaceId]
  optimizationConfig: (scenarioId, workspaceId = 0) => 
    ['optimizationConfig', scenarioId, workspaceId],
  
  // Simulation config: ['simulationConfig', scenarioId, workspaceId]
  simulationConfig: (scenarioId, workspaceId = 0) => 
    ['simulationConfig', scenarioId, workspaceId],
  
  // Scenario bundle: ['scenarioBundle', scenarioId, workspaceId]
  scenarioBundle: (scenarioId, workspaceId = 0) => 
    ['scenarioBundle', scenarioId, workspaceId],
  
  // Heatmap data: ['heatmapData', scenarioId, mode, workspaceId]
  heatmapData: (scenarioId, mode = 'service', workspaceId = 0) => 
    ['heatmapData', scenarioId, mode, workspaceId],
  
  // Facilities list: ['facilities', workspaceId]
  facilities: (workspaceId = 0) => 
    ['facilities', workspaceId],
}

/** The design-under-uncertainty result: ['designResult', scenarioId, workspaceId]. */
export const designResultKey = (scenarioId, workspaceId) =>
  ['designResult', Number(scenarioId), Number(workspaceId)]
